const REGISTRY_KEY = "bunkerfall.campaign-registry";
const LEGACY_CAMPAIGN_KEY = "bunkerfall.campaign";
const LEGACY_ACCESS_KEY = "bunkerfall.campaign-key";

export interface StoredCampaign {
  id: string;
  name: string;
  accessKey?: string;
  pendingAccessKey?: string;
  lastOpenedAt: string;
}

export interface CampaignRegistry {
  version: 1;
  activeId: string | null;
  entries: Record<string, Omit<StoredCampaign, "id">>;
}

const emptyRegistry = (): CampaignRegistry => ({
  version: 1,
  activeId: null,
  entries: {},
});

let memoryRegistry = emptyRegistry();
let persistentStorageAvailable = true;

function storage(): Storage | null {
  if (!persistentStorageAvailable) return null;
  try {
    return window.localStorage;
  } catch {
    persistentStorageAvailable = false;
    return null;
  }
}

function validRegistry(value: unknown): value is CampaignRegistry {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<CampaignRegistry>;
  const entriesValid = Boolean(candidate.entries)
    && typeof candidate.entries === "object"
    && Object.values(candidate.entries).every((entry) => {
      if (!entry || typeof entry !== "object") return false;
      const stored = entry as Partial<Omit<StoredCampaign, "id">>;
      return typeof stored.name === "string"
        && typeof stored.lastOpenedAt === "string"
        && (stored.accessKey === undefined || typeof stored.accessKey === "string")
        && (stored.pendingAccessKey === undefined || typeof stored.pendingAccessKey === "string");
    });
  return (
    candidate.version === 1 &&
    (candidate.activeId === null || typeof candidate.activeId === "string") &&
    entriesValid
  );
}

function persist(registry: CampaignRegistry): CampaignRegistry {
  memoryRegistry = registry;
  const target = storage();
  if (!target) return registry;
  try {
    target.setItem(REGISTRY_KEY, JSON.stringify(registry));
  } catch {
    // Safari private mode, quota policy, and embedded contexts can expose the
    // Storage object but reject writes. Preserve capabilities for this tab.
    persistentStorageAvailable = false;
  }
  return registry;
}

export function readCampaignRegistry(): CampaignRegistry {
  const target = storage();
  if (!target) return memoryRegistry;
  let stored: string | null;
  try {
    stored = target.getItem(REGISTRY_KEY);
  } catch {
    persistentStorageAvailable = false;
    return memoryRegistry;
  }
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      if (validRegistry(parsed)) {
        memoryRegistry = parsed;
        return parsed;
      }
    } catch {
      // A damaged local registry must not prevent a new community being created.
    }
  }

  let legacyId: string | null = null;
  let legacyAccessKey: string | null = null;
  try {
    legacyId = target.getItem(LEGACY_CAMPAIGN_KEY);
    legacyAccessKey = target.getItem(LEGACY_ACCESS_KEY);
  } catch {
    persistentStorageAvailable = false;
    return memoryRegistry;
  }
  const migrated = emptyRegistry();
  if (legacyId) {
    migrated.activeId = legacyId;
    migrated.entries[legacyId] = {
      name: "Recovered community",
      ...(legacyAccessKey ? { accessKey: legacyAccessKey } : {}),
      lastOpenedAt: new Date().toISOString(),
    };
  }
  persist(migrated);
  try {
    target.removeItem(LEGACY_CAMPAIGN_KEY);
    target.removeItem(LEGACY_ACCESS_KEY);
  } catch {
    persistentStorageAvailable = false;
  }
  return migrated;
}

export function resetCampaignStorageForTests(): void {
  memoryRegistry = emptyRegistry();
  persistentStorageAvailable = true;
}

export function listStoredCampaigns(): StoredCampaign[] {
  const registry = readCampaignRegistry();
  return Object.entries(registry.entries)
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt));
}

export function activeCampaignId(): string | null {
  const registry = readCampaignRegistry();
  return registry.activeId && registry.entries[registry.activeId]
    ? registry.activeId
    : null;
}

export function campaignCapability(id: string): string | undefined {
  return readCampaignRegistry().entries[id]?.accessKey;
}

export function pendingCampaignCapability(id: string): string | undefined {
  return readCampaignRegistry().entries[id]?.pendingAccessKey;
}

export function stageCampaignCapabilityRotation(
  id: string,
  replacementAccessKey: string,
): CampaignRegistry {
  const registry = readCampaignRegistry();
  const entry = registry.entries[id];
  if (!entry) return registry;
  entry.pendingAccessKey = replacementAccessKey;
  return persist(registry);
}

export function commitCampaignCapabilityRotation(id: string): CampaignRegistry {
  const registry = readCampaignRegistry();
  const entry = registry.entries[id];
  if (!entry?.pendingAccessKey) return registry;
  entry.accessKey = entry.pendingAccessKey;
  delete entry.pendingAccessKey;
  entry.lastOpenedAt = new Date().toISOString();
  return persist(registry);
}

export function clearPendingCampaignCapability(id: string): CampaignRegistry {
  const registry = readCampaignRegistry();
  const entry = registry.entries[id];
  if (entry) delete entry.pendingAccessKey;
  return persist(registry);
}

export function recordCampaign(
  campaign: { id: string; name: string; access_key?: string },
  options: { activate?: boolean; accessKey?: string } = {},
): CampaignRegistry {
  const registry = readCampaignRegistry();
  const current = registry.entries[campaign.id];
  const accessKey = options.accessKey ?? campaign.access_key ?? current?.accessKey;
  const pendingAccessKey = campaign.access_key ? undefined : current?.pendingAccessKey;
  registry.entries[campaign.id] = {
    name: campaign.name,
    ...(accessKey ? { accessKey } : {}),
    ...(pendingAccessKey ? { pendingAccessKey } : {}),
    lastOpenedAt: new Date().toISOString(),
  };
  if (options.activate !== false) registry.activeId = campaign.id;
  return persist(registry);
}

export function selectCampaign(id: string | null): CampaignRegistry {
  const registry = readCampaignRegistry();
  registry.activeId = id && registry.entries[id] ? id : null;
  const activeEntry = registry.activeId
    ? registry.entries[registry.activeId]
    : undefined;
  if (activeEntry) activeEntry.lastOpenedAt = new Date().toISOString();
  return persist(registry);
}

export function removeCampaignFromDevice(id: string): CampaignRegistry {
  const registry = readCampaignRegistry();
  delete registry.entries[id];
  if (registry.activeId === id) registry.activeId = null;
  return persist(registry);
}

export function recoveryKeyFor(id: string): string | null {
  const accessKey = campaignCapability(id);
  return accessKey ? `BUNKERFALL1:${id}:${accessKey}` : null;
}

export function parseRecoveryKey(value: string): {
  campaignId: string;
  accessKey: string;
} | null {
  const match = value.trim().match(
    /^BUNKERFALL1:([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}):([^:\s]{20,})$/i,
  );
  return match?.[1] && match[2]
    ? { campaignId: match[1], accessKey: match[2] }
    : null;
}

export const campaignStorageKeys = {
  registry: REGISTRY_KEY,
  legacyCampaign: LEGACY_CAMPAIGN_KEY,
  legacyAccess: LEGACY_ACCESS_KEY,
} as const;
